import { logger } from '@adornis/base/logging.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { matchingConfig } from '@adornis/bh-matching-config/config.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-matching-user/checkAuthorizedMiddleware.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { getMenteeUserClass } from '@adornis/bh-matching-user/user-class.js';
import { scopedCollection } from '@adornis/bh-util/server/scoped-collection.js';
import { delay, isDefined } from '@adornis/bh-util/util.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { assertInsideFileStore } from '@adornis/buchungsheld-db/mails/file-attachment.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { prepareMessage } from '@adornis/mails/server/communication.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { concatMap, filter, from, map, switchMap } from 'rxjs';
import { BuddyMailAttachment } from '../entities/attachment.js';
import { BuddyMailTemplate, convertUserToMailVarObject } from '../entities/mail-templates.js';
import { mmr } from '../mail-registry.js';
import { getBuddyMailAttachmentsByIDs } from './attachment.js';
import { assembleBuddyMail } from '../mails/assemble-mail.js';

type Pairs = { mentee: EntityData<BuddyUser>; mentor: EntityData<BuddyUser>; userClass: string | undefined };

export const getUserMailVars = registerQuery({
  operationName: 'getUserMailVars2',
  middlewares: [
    checkAuthorizedMiddleware(
      ({ user, params }) =>
        user._id === params.userID || user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS),
    ),
  ],
  type: () => String,
  params: [{ name: 'userID', type: () => String }],
  resolve: (userID: string) => async () => {
    const currentUser = await getMyself()({ _id: 1, roles: selectionSet(() => AdornisRoleToContextsWrapper, 5) });

    if (!currentUser) throw new Error('not logged in');

    const userData = await loadMongoData(User, userID);
    if (!userData) throw new Error('user not found');

    const user = constructValue(userData);
    const userType = user instanceof BuddyUserMentee ? 'mentee' : 'mentor';
    const vars = convertUserToMailVarObject(user, userType);
    return JSON.stringify(vars);
  },
});

async function getUsersPairsByMatches(matchIDs: string[], userClass?: string) {
  const matches = await (await scopedCollection('Match')).find({ _id: { $in: matchIDs } }).toArray();
  const userCollection = await scopedCollection<BuddyUser>(BuddyUser._class);
  const pairs = (
    await Promise.all(
      matches.map(async match => {
        const mentee = await userCollection.findOne({ _id: (match as any).menteeID });
        const mentor = await userCollection.findOne({ _id: (match as any).mentorID });
        if (!mentee || !mentor) {
          logger.warn({ match, mentee, mentor }, 'mentee or mentor not found');
          return null;
        }
        return { mentee, mentor, userClass };
      }),
    )
  ).filter(isDefined);
  return pairs;
}

export const sendFreeFormMailToBuddyUsers = registerSubscription({
  operationName: 'sendFreeFormMailToBuddyUsers',
  resolve: (matchIDs: string[], mailTemplate: BuddyMailTemplate, userClass?: string) => () => {
    return from(
      (async (): Promise<[Pairs[], BuddyMailAttachment[]]> => {
        const currentUser = await getMyself<BuddyUser>()({
          roles: selectionSet(() => AdornisRoleToContextsWrapper, 2),
        });
        if (!context.serverContext && !currentUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
          throw new Error('No permission to send mails to users');

        const pairs = (await getUsersPairsByMatches(matchIDs, userClass)) as Pairs[];

        const attachments = (await getBuddyMailAttachmentsByIDs(mailTemplate.activatedAttachments)(
          selectionSet(() => BuddyMailAttachment, 2),
        )) as BuddyMailAttachment[];

        return [pairs, attachments];
      })(),
    ).pipe(
      switchMap(([pairs, attachments]) =>
        from(pairs).pipe(
          concatMap(async (pair, i) => {
            // dont bulk send all emails, especially for larger amounts
            await delay(100);
            const mailVars = {
              ...(pair.mentee ? convertUserToMailVarObject(constructValue<BuddyUser>(pair.mentee), 'mentee') : {}),
              ...(pair.mentor ? convertUserToMailVarObject(constructValue<BuddyUser>(pair.mentor), 'mentor') : {}),
            };
            const targetMailTargets = (
              pair.userClass
                ? pair.userClass.includes('Mentee')
                  ? [pair.mentee?.email]
                  : [pair.mentor?.email]
                : [pair.mentee?.email, pair.mentor?.email]
            ).filter(Boolean) as string[];

            // send mail to all recipients standalone
            const m = await mailer;
            const { subject, content } = await assembleBuddyMail({ template: mailTemplate, infos: mailVars });
            const message = await prepareMessage({
              html: content ?? '',
              subject: subject ?? '',
              attachments: await Promise.all(
                attachments.map(async attachment => ({
                  filename: `${attachment.meta?.fileName ?? 'file'}.${attachment.meta?.extension ?? 'txt'}`,
                  path: await assertInsideFileStore(attachment),
                })),
              ),
            });
            for await (const targetMail of targetMailTargets) {
              // Don't let a single bad recipient (malformed address, whitelist
              // violation, SMTP rejection…) kill the rest of the batch.
              try {
                await m.sendMail(message, new Recipient([targetMail], undefined, undefined));
              } catch (err) {
                logger.warn({ err, targetMail }, 'sendFreeFormMailToBuddyUsers: skipped recipient after send error');
              }
            }

            // send copy (for first pair) — fire-and-forget, but log on error
            // instead of producing an unhandled rejection.
            const copyAddress = matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS');
            if (i === 0 && copyAddress) {
              message.subject = '[Kopie] ' + message.subject;
              m.sendMail(message, new Recipient([copyAddress], undefined, undefined)).catch(err =>
                logger.warn({ err, copyAddress }, 'sendFreeFormMailToBuddyUsers: copy send failed'),
              );
            }

            return i + 1;
          }),
        ),
      ),
    );
  },
  type: () => String,
  params: [
    { name: 'mailAddresses', type: () => [String] },
    { name: 'mailTemplate', type: () => BuddyMailTemplate },
    { name: 'userClass', type: () => String },
  ],
});

export const sendFreeFormMailToSingleUser = registerMutation({
  operationName: 'sendFreeFormMailToSingleUser',
  resolve: (userID: string, mailTemplate: BuddyMailTemplate) => async () => {
    const currentUser = await getMyself<BuddyUser>()({ roles: selectionSet(() => AdornisRoleToContextsWrapper, 2) });
    if (!context.serverContext && !currentUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
      throw new Error('No permission to send mails to users');

    const userCol = mongo().collection(User._collectionName);
    const user = await userCol.findOne({ _id: userID as any });
    if (!user) throw new Error('User not found: ' + userID);

    const userType = user._class === getMenteeUserClass()._class ? 'mentee' : 'mentor';
    const mailVars = convertUserToMailVarObject(constructValue<BuddyUser>(user), userType);
    const m = await mailer;
    const { subject, content } = await assembleBuddyMail({ template: mailTemplate, infos: mailVars });
    const message = await prepareMessage({
      html: content ?? '',
      subject: subject ?? '',
      attachments: [],
    });
    await m.sendMail(message, new Recipient([user.email], undefined, undefined));
    const copyAddress = matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS');
    if (copyAddress) {
      message.subject = '[Kopie] ' + message.subject;
      m.sendMail(message, new Recipient([copyAddress], undefined, undefined));
    }
  },
  type: () => String,
  params: [
    { name: 'userID', type: () => String },
    { name: 'mailTemplate', type: () => BuddyMailTemplate },
  ],
});

export const sendFreeFormMailToMatch = registerSubscription({
  operationName: 'sendFreeFormMailToMatch',
  resolve: (matchingID: string, mailTemplate: BuddyMailTemplate, userClass?: string) => () => {
    return from(
      (async (): Promise<
        [[EntityData<BuddyUser>, EntityData<BuddyUser>], BuddyMailAttachment[], string | undefined]
      > => {
        const currentUser = await getMyself<BuddyUser>()({
          roles: selectionSet(() => AdornisRoleToContextsWrapper, 2),
        });
        if (!context.serverContext && !currentUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
          throw new Error('No permission to send mails to users');

        const match = (await (await scopedCollection('Match')).findOne({ _id: matchingID })) as any;
        if (!match) throw new Error('Match not found');
        const userCollection = await scopedCollection<BuddyUser>(BuddyUser._class);

        const mentor = await userCollection.findOne<BuddyUser>({ _id: match.mentorID });
        if (!mentor) throw new Error('Mentor not found');

        const mentee = await userCollection.findOne<BuddyUser>({ _id: match.menteeID });
        if (!mentee) throw new Error('Mentee not found');

        const attachments = await getBuddyMailAttachmentsByIDs(mailTemplate.activatedAttachments)(
          selectionSet(() => BuddyMailAttachment, 2),
        );

        return [[mentor, mentee], attachments as any, userClass];
      })(),
    ).pipe(
      switchMap(([users, attachments, userClass]) =>
        from(users).pipe(
          map(user => <const>[user, attachments, users]),
          filter(([user, _, __]) => (userClass ? user._class === userClass : false)),
          concatMap(async ([user, attachments, users]) => {
            // dont bulk send all emails, especially for larger amounts
            await delay(100);

            try {
              const { subject, content } = await assembleBuddyMail({ template: mailTemplate, infos: user as any });
              await (
                await mailer
              ).sendMail(
                await prepareMessage({
                  html: content ?? '',
                  subject: subject ?? '',
                  attachments: await Promise.all(
                    attachments.map(async attachment => ({
                      filename: `${attachment.meta?.fileName ?? 'file'}.${attachment.meta?.extension ?? 'txt'}`,
                      path: await assertInsideFileStore(attachment),
                    })),
                  ),
                }),
                new Recipient(user.email, undefined, undefined),
              );
            } catch (err) {
              logger.warn({ err, email: user.email }, 'sendFreeFormMailToMatch: skipped recipient after send error');
            }
            return (users.indexOf(user) + 1).toString();
          }),
        ),
      ),
    );
  },
  type: () => String,
  params: [
    { name: 'mailAddresses', type: () => [String] },
    { name: 'mailTemplate', type: () => BuddyMailTemplate },
    { name: 'userClass', type: () => String },
  ],
});

async function sendRegistrationMailServer(user: BuddyUser) {
  try {
    const m = await import('@adornis/bh-matching-config/config.js');
    const recipients = [
      new Recipient(user.email, m.matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS') ?? undefined, undefined),
    ];
    const mailOptions = { recipients, mailer: await mailer };

    if (user instanceof BuddyUserMentor) return await mmr.registrationMentor(user._id).send(mailOptions);
    if (user instanceof BuddyUserMentee) return await mmr.registrationMentee(user._id).send(mailOptions);

    throw new Error('user instance unknown ' + user._class);
  } catch (err) {
    logger.error({ err, userID: user._id }, 'Couldnt send registration mail to user');
  }
}

export const sendRegistrationMail = registerMutation({
  operationName: 'sendRegistrationMail',
  type: () => String,
  params: [{ name: 'userID', type: () => String }],
  resolve: (userID: string) => async () => {
    const storedUserData = await mongo().collection<EntityData<BuddyUser>>(BuddyUser._collectionName).findOne({
      _id: userID,
    });
    if (!storedUserData) throw new Error('user not found');

    const currentUser = await getMyself<BuddyUser>()({ _id: 1 });
    const createdAt = storedUserData.createdAt;
    const ageInMilliseconds = createdAt instanceof Date ? Date.now() - createdAt.getTime() : Number.POSITIVE_INFINITY;
    const wasCreatedRecently = ageInMilliseconds >= 0 && ageInMilliseconds <= 5 * 60 * 1000;
    if (currentUser?._id !== storedUserData._id && !wasCreatedRecently)
      throw new Error('No permission to send this registration mail');

    const storedUser = constructValue<BuddyUser>(storedUserData);
    return runInContext(serverContext.sessionID, async () => {
      await sendRegistrationMailServer(storedUser);
      return 'done';
    });
  },
});
